import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ChevronRight as ChevronRightIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import StoreCustomerSectionCard from './StoreCustomerSectionCard';
import { formatCalendarDate, formatClockTime } from '../../utils/dateFormatUtils';
import { getAppointmentStatusKey } from '../../utils/appointmentStatusUtils';
import { buildManagementAppointmentsPath } from '../../utils/staffNotificationNavigation';
import { BRAND, brandAlpha } from '../../theme/brandTokens';
import useBrandPalette from '../../theme/useBrandPalette';

const STATUS_COLORS = {
  pending: '#f59e0b',
  confirmed: '#3b82f6',
  completed: '#10b981',
  cancelled: '#ef4444',
  noShow: '#e11d48',
  // awaitingPayment uses the store accent (resolved at render: the chip runs alpha() on these).
};

const formatPeriod = (period) => {
  if (!period?.from) {
    return null;
  }

  const from = formatClockTime(period.from);
  const to = period.to ? formatClockTime(period.to) : '';
  if (from === '—' && !to) {
    return null;
  }

  return to && to !== '—' ? `${from} – ${to}` : from;
};

const StoreCustomerUpcomingAppointments = ({
  storeId,
  customerId,
  appointments,
  loading,
  error,
  canOpenAgenda,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const brand = useBrandPalette();
  const statusColors = { ...STATUS_COLORS, awaitingPayment: brand.accent };

  const openAppointment = (appointment) => {
    if (!canOpenAgenda || !appointment?.appointmentId) {
      return;
    }

    const from = `/management/stores/${storeId}/customers/${customerId}`;
    navigate(buildManagementAppointmentsPath({
      appointmentId: appointment.appointmentId,
      date: appointment.date,
      from,
    }));
  };

  const handleKeyDown = (event, appointment) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openAppointment(appointment);
    }
  };

  return (
    <StoreCustomerSectionCard
      title={t('customersManagement.details.upcoming.title')}
      icon={<EventNoteIcon fontSize="small" />}
    >
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : null}

      {loading && !appointments.length && !error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} sx={{ color: BRAND.accentText }} />
        </Box>
      ) : null}

      {!loading && !error && appointments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('customersManagement.details.upcoming.empty')}
        </Typography>
      ) : null}

      {appointments.length > 0 ? (
        <Stack
          divider={(
            <Box sx={{ borderBottom: '1px solid', borderColor: BRAND.lineSoft }} />
          )}
          sx={{ opacity: loading ? 0.6 : 1 }}
        >
          {appointments.map((appointment) => {
            const statusKey = getAppointmentStatusKey(appointment.status);
            const statusColor = statusColors[statusKey] || '#9e9e9e';
            const timeLabel = formatPeriod(appointment.period);
            const clickable = Boolean(canOpenAgenda && appointment.appointmentId);

            return (
              <Box
                key={appointment.appointmentId}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => openAppointment(appointment) : undefined}
                onKeyDown={clickable ? (event) => handleKeyDown(event, appointment) : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1.25,
                  cursor: clickable ? 'pointer' : 'default',
                  borderRadius: 1,
                  mx: -0.5,
                  px: 0.5,
                  '&:focus-visible': clickable ? {
                    outline: `2px solid ${BRAND.accent}`,
                    outlineOffset: 2,
                  } : undefined,
                  '&:hover': clickable ? {
                    backgroundColor: brandAlpha('accent', 0.06),
                  } : undefined,
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: statusColor,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: BRAND.text,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {appointment.servicesSummary || t('managementAppointments.unnamedService')}
                    </Typography>
                    <Chip
                      label={t(`customer.appointments.status.${statusKey}`)}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.675rem',
                        fontWeight: 600,
                        bgcolor: alpha(statusColor, 0.1),
                        color: statusColor,
                        flexShrink: 0,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  </Stack>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {[
                      formatCalendarDate(appointment.date),
                      timeLabel,
                      appointment.teamMemberName || t('managementAppointments.unnamedProfessional'),
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                {clickable ? (
                  <ChevronRightIcon sx={{ color: BRAND.accentText, flexShrink: 0 }} />
                ) : null}
              </Box>
            );
          })}
        </Stack>
      ) : null}
    </StoreCustomerSectionCard>
  );
};

export default StoreCustomerUpcomingAppointments;
